/**
 * Local persistence for an in-progress exam attempt.
 *
 * Why: even with retry logic, in-memory `answers` / `failedAnswersRef`
 * disappear if the student refreshes, closes the tab, or hits a JS crash.
 * This helper persists both to localStorage under a key scoped by the
 * server `attemptId`, so on reload we can rehydrate the UI and silently
 * replay any answers the server never acknowledged.
 *
 * Storage shape (per attempt):
 *   {
 *     v: 1,
 *     examId: string,
 *     attemptId: string,
 *     answers: { [questionId]: selectedOption },     // student's current picks
 *     failedAnswers: Array<{ questionId, selectedOption }>, // queued for retry
 *     savedAt: ISO string
 *   }
 *
 * Notes:
 * - Keyed by attemptId so concurrent attempts (different exams) never
 *   overwrite each other.
 * - We expose a generic prefix so other helpers can sweep stale drafts.
 * - All operations are best-effort — localStorage may throw in private
 *   mode or when quota is exceeded; we swallow those errors because
 *   losing a draft is strictly less bad than crashing the exam page.
 */

const PREFIX = "toeic.examDraft.v1.";
const SCHEMA_VERSION = 1;

const keyFor = (attemptId) => `${PREFIX}${attemptId}`;

export const saveExamDraft = (
  attemptId,
  {
    examId,
    answers,
    failedAnswers,
    readingRemainingSeconds,
    listeningProgress,
  }
) => {
  if (!attemptId) return;
  try {
    const existing = loadExamDraft(attemptId);
    const payload = {
      v: SCHEMA_VERSION,
      examId: examId || null,
      attemptId,
      answers: answers || {},
      failedAnswers: Array.isArray(failedAnswers) ? failedAnswers : [],
      // Persist paused Reading countdown so resume restores exact remaining
      // time instead of subtracting wall-clock time while away.
      readingRemainingSeconds:
        Number.isFinite(readingRemainingSeconds)
          ? readingRemainingSeconds
          : (existing?.readingRemainingSeconds ?? null),
      // Furthest Listening cursor reached so resume does not jump backward
      // to older unanswered listening questions after audio auto-advances.
      listeningProgress:
        listeningProgress &&
        Number.isInteger(listeningProgress.partIndex) &&
        Number.isInteger(listeningProgress.groupIndex)
          ? {
              partIndex: listeningProgress.partIndex,
              groupIndex: listeningProgress.groupIndex,
            }
          : (existing?.listeningProgress ?? null),
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(keyFor(attemptId), JSON.stringify(payload));
  } catch (_) {
    // ignored — private mode / quota — nothing we can do here
  }
};

export const loadExamDraft = (attemptId) => {
  if (!attemptId) return null;
  try {
    const raw = localStorage.getItem(keyFor(attemptId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== SCHEMA_VERSION) return null;
    return parsed;
  } catch (_) {
    return null;
  }
};

export const clearExamDraft = (attemptId) => {
  if (!attemptId) return;
  try {
    localStorage.removeItem(keyFor(attemptId));
  } catch (_) {
    // ignored
  }
};

/**
 * Remove drafts older than `maxAgeMs` (default 7 days). Called on mount
 * to keep localStorage tidy if a student starts many attempts and never
 * finishes them.
 */
export const sweepStaleExamDrafts = (maxAgeMs = 7 * 24 * 60 * 60 * 1000) => {
  try {
    const now = Date.now();
    const stale = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(PREFIX)) continue;
      try {
        const parsed = JSON.parse(localStorage.getItem(k));
        const savedAt = parsed?.savedAt ? Date.parse(parsed.savedAt) : 0;
        if (!savedAt || now - savedAt > maxAgeMs) {
          stale.push(k);
        }
      } catch (_) {
        stale.push(k); // unparseable → drop
      }
    }
    stale.forEach((k) => localStorage.removeItem(k));
  } catch (_) {
    // ignored
  }
};
